import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../../utils/api'
import { QuoteNotesCard } from './QuoteNotes'
import DealLink from '../shared/DealLink.jsx'

// Design System (matching warehouse components)
const theme = {
  colors: {
    charcoal: '#222725',
    white: '#FFFFFF',
    grey100: '#F8F9F8',
    grey200: '#E7E5E4',
    grey300: '#D6D3D1',
    grey400: '#A8A29E',
    grey500: '#78716C',
    greenOnions: '#3BB273',
    greenLight: '#E8F5EE',
    amberLight: '#FFF9E6',
    imBlue: '#4E8098',
    blueLight: '#EDF3F6'
  },
  radius: { sm: '6px', md: '10px', lg: '16px' },
  shadows: { sm: '0 1px 2px rgba(0,0,0,0.05)' }
}

const Card = ({ children, style = {} }) => (
  <div style={{
    background: theme.colors.white,
    borderRadius: theme.radius.lg,
    boxShadow: theme.shadows.sm,
    border: `1px solid ${theme.colors.grey200}`,
    ...style
  }}>
    {children}
  </div>
)

const Badge = ({ children, color = 'grey' }) => {
  const colors = {
    grey: { bg: theme.colors.grey200, text: theme.colors.grey500 },
    green: { bg: theme.colors.greenLight, text: theme.colors.greenOnions },
    amber: { bg: theme.colors.amberLight, text: '#B8860B' },
    blue: { bg: theme.colors.blueLight, text: theme.colors.imBlue }
  }
  const c = colors[color] || colors.grey
  return (
    <span style={{
      display: 'inline-flex', padding: '4px 10px', borderRadius: '20px',
      fontSize: '12px', fontWeight: 600, background: c.bg, color: c.text
    }}>
      {children}
    </span>
  )
}

const Spinner = ({ size = 20 }) => (
  <div style={{
    width: size, height: size,
    border: `2px solid ${theme.colors.grey200}`,
    borderTopColor: theme.colors.charcoal,
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite'
  }}>
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
)

// How long ago an edit landed, for the "left half-done N ago" column.
function formatTimeAgo(dateString) {
  if (!dateString) return '-'
  const diffMs = Date.now() - new Date(dateString).getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)
  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  return `${diffDays}d ago`
}

// One item's status in the expanded view: a saved outcome wins, otherwise its
// arrival state decides whether it is testable-but-untested or not yet in.
function itemStatusChip(item) {
  if (item.draftStatus === 'complete') return { label: 'Tested', color: 'green' }
  if (item.draftStatus === 'in_progress') return { label: 'In progress', color: 'blue' }
  if (item.state === 'pending') return { label: 'Not received', color: 'grey' }
  return { label: 'Untested', color: 'amber' }
}

function PartiallyTested() {
  const navigate = useNavigate()
  const [deals, setDeals] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [expandedId, setExpandedId] = useState(null)

  useEffect(() => { loadPartialTrades() }, [])

  async function loadPartialTrades() {
    setLoading(true)
    setError(null)
    try {
      const response = await api.get('/api/warehouse/testing/partial-drafts')
      setDeals(response.deals || [])
    } catch (err) {
      console.error('Failed to load partially-tested trades:', err)
      setError(err.message || 'Failed to load partially-tested trades')
    } finally {
      setLoading(false)
    }
  }

  function toggleRow(id) {
    setExpandedId(prev => (prev === id ? null : id))
  }

  function openInTesting(id) {
    navigate(`/warehouse/testing?dealId=${encodeURIComponent(id)}`)
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
        <Spinner size={28} />
      </div>
    )
  }

  return (
    <div>
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ margin: '0 0 4px', fontSize: '20px', color: theme.colors.charcoal }}>
          Partially Tested Trades
        </h2>
        <p style={{ margin: 0, color: theme.colors.grey500, fontSize: '14px' }}>
          Trades left half-tested — open one to finish the remaining items.
        </p>
      </div>

      {error && (
        <Card style={{ padding: '16px', marginBottom: '16px', borderColor: '#E5625E', color: '#E5625E' }}>
          {error}
        </Card>
      )}

      {deals.length === 0 && !error ? (
        <Card style={{ padding: '40px', textAlign: 'center', color: theme.colors.grey500 }}>
          No partially-tested trades — every started deal is either fully submitted or untouched.
        </Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {deals.map(deal => (
            <DealRow
              key={deal.id}
              deal={deal}
              expanded={expandedId === deal.id}
              onToggle={() => toggleRow(deal.id)}
              onOpen={() => openInTesting(deal.id)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function DealRow({ deal, expanded, onToggle, onOpen }) {
  const progressPct = deal.testableCount > 0
    ? Math.round((deal.testedCount / deal.testableCount) * 100)
    : 0

  // The quote couldn't be loaded this run (a Prism blip). We still list the deal
  // rather than hide it — the operator opens it on Testing, which fetches afresh.
  if (deal.unresolved) {
    return (
      <Card>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '16px',
          padding: '16px 20px', flexWrap: 'wrap'
        }}>
          <div style={{ flex: '1 1 200px', minWidth: 0 }}>
            <div style={{ fontWeight: 600, color: theme.colors.charcoal }}><DealLink dealId={deal.id} reference={deal.reference} newTab /></div>
            <div style={{ color: theme.colors.grey500, fontSize: '13px' }}>
              Details couldn’t be loaded — open to view.
            </div>
          </div>
          <div style={{ flex: '0 0 140px', fontSize: '13px', color: theme.colors.grey500 }}>
            {formatTimeAgo(deal.lastEditedAt)}
            {deal.lastEditedBy ? ` · ${deal.lastEditedBy}` : ''}
          </div>
          <button className="btn btn-sm btn-primary" onClick={onOpen}>
            Open in Testing
          </button>
        </div>
      </Card>
    )
  }

  return (
    <Card>
      <div
        onClick={onToggle}
        style={{
          display: 'flex', alignItems: 'center', gap: '16px',
          padding: '16px 20px', cursor: 'pointer', flexWrap: 'wrap'
        }}
      >
        <div style={{ flex: '1 1 200px', minWidth: 0 }}>
          <div style={{ fontWeight: 600, color: theme.colors.charcoal }}><DealLink dealId={deal.id} reference={deal.reference} newTab /></div>
          <div style={{ color: theme.colors.grey500, fontSize: '13px' }}>{deal.customerName}</div>
        </div>

        <div style={{ flex: '0 0 160px' }}>
          <div style={{ fontSize: '13px', color: theme.colors.grey500, marginBottom: '4px' }}>
            {deal.testedCount} / {deal.testableCount} tested
          </div>
          <div style={{ height: '6px', background: theme.colors.grey200, borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{ width: `${progressPct}%`, height: '100%', background: theme.colors.greenOnions }} />
          </div>
        </div>

        <div style={{ flex: '0 0 auto' }}>
          <Badge color="amber">{deal.untestedCount} left</Badge>
        </div>

        <div style={{ flex: '0 0 140px', fontSize: '13px', color: theme.colors.grey500 }}>
          {formatTimeAgo(deal.lastEditedAt)}
          {deal.lastEditedBy ? ` · ${deal.lastEditedBy}` : ''}
        </div>

        <button
          className="btn btn-sm btn-primary"
          onClick={(e) => { e.stopPropagation(); onOpen() }}
        >
          Open in Testing
        </button>
      </div>

      {expanded && (
        <div style={{ borderTop: `1px solid ${theme.colors.grey200}`, padding: '12px 20px' }}>
          <QuoteNotesCard dealId={deal.id} style={{ marginBottom: '12px' }} />
          {deal.items.map(item => {
            const chip = itemStatusChip(item)
            return (
              <div
                key={item.index}
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '8px 0', fontSize: '14px', color: theme.colors.charcoal
                }}
              >
                <span>{item.index + 1}. {item.name}</span>
                <Badge color={chip.color}>{chip.label}</Badge>
              </div>
            )
          })}
        </div>
      )}
    </Card>
  )
}

export default PartiallyTested
